import { motion } from 'framer-motion'
import { IPhoneNumber, IPhoneNumberGroup } from '../../interface'
import { ChevronRightIcon } from '@forwardflow/icons'
import { useEffect, useState, useMemo } from 'react'
import { usePhoneNumbers } from '../../hooks'
import { PhoneNumbers } from '../PhoneNumbers'
import { PhoneNumberSearchInput } from '../PhoneNumberSearchInput'
import { AllocateUnusedNumbersView } from './AllocateUnusedNumbersView'
import { BuyNewNumbersView } from './BuyNewNumbersView'
import { DeletePhoneNumberModal } from '../modals/DeletePhoneNumberModal'
import { filterPhoneNumberGroups } from '../../utils'
import { message } from 'antd'

export const CampaignNumberGroupTabs = ({
   numberGroups,
   campaignId,
}: {
   numberGroups: IPhoneNumberGroup[]
   campaignId: string
}) => {
   const { groupedInactivePhoneNumbers, removePhoneNumbers } =
      usePhoneNumbers()
   const [selectedTab, setSelectedTab] = useState<
      'list-of-numbers' | 'allocate-unused' | 'buy-new-numbers'
   >('list-of-numbers')
   const [searchQuery, setSearchQuery] = useState('')
   const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
   const [numberToDelete, setNumberToDelete] = useState<IPhoneNumber | null>(
      null
   )
   const [isDeleting, setIsDeleting] = useState(false)

   useEffect(() => {
      setSelectedTab('list-of-numbers')
      setSearchQuery('')
   }, [campaignId])

   const handleDeleteClick = (number: IPhoneNumber) => {
      setNumberToDelete(number)
      setIsDeleteModalOpen(true)
   }

   const handleConfirmDelete = async () => {
      if (numberToDelete) {
         setIsDeleting(true)
         try {
            await removePhoneNumbers([numberToDelete.phoneNumber.phoneNumber])
            message.success('Phone number removed successfully')
            setIsDeleteModalOpen(false)
            setNumberToDelete(null)
         } catch (error) {
            console.error('Error removing phone number:', error)
         } finally {
            setIsDeleting(false)
         }
      }
   }

   const handleCloseDeleteModal = () => {
      if (isDeleting) return
      setIsDeleteModalOpen(false)
      setNumberToDelete(null)
   }

   // Filter campaign numbers
   const filteredCampaignGroups = useMemo(
      () => filterPhoneNumberGroups(numberGroups, searchQuery),
      [numberGroups, searchQuery]
   )

   // Check if this is the last number in the campaign
   const isLastNumber = useMemo(() => {
      return (
         filteredCampaignGroups.reduce(
            (total, group) => total + group.numbers.length,
            0
         ) === 1
      )
   }, [filteredCampaignGroups])

   if (selectedTab === 'list-of-numbers')
      return (
         <div className="flex flex-col gap-y-6">
            <div className="flex items-center gap-x-3 pr-3">
               <motion.button
                  className="bg-[#9061DA]/20 rounded-lg px-6 py-3 flex items-center justify-between text-white whitespace-nowrap hover:bg-[#9061DA]/30 flex-1"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedTab('buy-new-numbers')}
               >
                  <span>Purchase new numbers</span>
                  <ChevronRightIcon className="text-white w-4 h-4 ml-2" />
               </motion.button>
               <motion.button
                  className="bg-[#9061DA]/20 rounded-lg px-6 py-3 flex items-center justify-between text-white whitespace-nowrap hover:bg-[#9061DA]/30 flex-1"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedTab('allocate-unused')}
               >
                  <span>Allocate unused numbers</span>
                  <ChevronRightIcon className="text-white w-4 h-4 ml-2" />
               </motion.button>
            </div>
            <PhoneNumberSearchInput
               value={searchQuery}
               onChange={setSearchQuery}
               placeholder="Search Phone numbers"
            />
            {filteredCampaignGroups.length > 0 ? (
               <PhoneNumbers
                  numberGroups={filteredCampaignGroups}
                  onDelete={handleDeleteClick}
               />
            ) : (
               <div className="flex items-center justify-center py-12">
                  <p className="text-white/40 text-sm">
                     No phone numbers found
                     {searchQuery && ` for "${searchQuery}"`}
                  </p>
               </div>
            )}
            <DeletePhoneNumberModal
               isOpen={isDeleteModalOpen}
               onClose={handleCloseDeleteModal}
               onConfirm={handleConfirmDelete}
               phoneNumber={numberToDelete || undefined}
               isLastNumber={isLastNumber}
               isLoading={isDeleting}
            />
         </div>
      )

   if (selectedTab === 'allocate-unused')
      return (
         <>
            <AllocateUnusedNumbersView
               groupedInactivePhoneNumbers={groupedInactivePhoneNumbers}
               campaignId={campaignId}
               setSelectedTab={(tab) =>
                  setSelectedTab(tab as 'list-of-numbers' | 'allocate-unused')
               }
               onNumbersSelected={(numbers) => {
                  console.log('Selected numbers for allocation:', numbers)
               }}
            />
         </>
      )

   if (selectedTab === 'buy-new-numbers')
      return (
         <BuyNewNumbersView
            setSelectedTab={(tab) =>
               setSelectedTab(
                  tab as
                     | 'list-of-numbers'
                     | 'allocate-unused'
                     | 'buy-new-numbers'
               )
            }
            onNumbersSelected={(numbers) => {
               console.log('Selected numbers for purchase:', numbers)
               // TODO: Handle number purchase
            }}
         />
      )
}
